import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { sponsors, images, companyInfo } from '../data/mock';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { ExternalLink, Award, Globe, Heart, Phone } from 'lucide-react';

const OurSponsorsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero - Compact */}
      <section className="pt-6 pb-10 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={images.clinical} alt="Sponsors" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/95 via-[#0a1628]/80 to-[#0a1628]/60" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-10">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Our <span className="text-[#87CEEB]">Sponsors</span></h1>
          <p className="text-base text-white/80">Working with leading pharmaceutical companies.</p>
        </div>
      </section>

      {/* Featured Sponsor - CDOC Medical Group */}
      <section className="py-8 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-lg font-bold text-center text-gray-900 mb-4">Featured <span className="text-[#1877F2]">Partner</span></h2>
          <Card className="border-0 shadow-lg rounded-xl overflow-hidden" data-testid="featured-sponsor-card">
            <div className="bg-gradient-to-r from-[#87CEEB] to-[#1877F2] p-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <img 
                    src={sponsors.featured.logo} 
                    alt={sponsors.featured.name}
                    className="h-12 w-auto bg-white rounded-lg p-1.5"
                  />
                  <div className="text-white">
                    <h3 className="text-base font-bold">{sponsors.featured.name}</h3>
                    <p className="text-white/90 text-xs">{sponsors.featured.description}</p>
                  </div>
                </div>
                <a href={sponsors.featured.website} target="_blank" rel="noopener noreferrer" data-testid="featured-sponsor-link">
                  <Button className="bg-white text-[#1877F2] px-4 py-2 rounded-full font-bold text-sm">Visit <ExternalLink className="ml-1 h-3 w-3" /></Button>
                </a>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Main Sponsor - Compact */}
      <section className="py-8 bg-[#F8FBFF]">
        <div className="max-w-3xl mx-auto px-4">
          <Card className="border-0 shadow-lg rounded-xl overflow-hidden" data-testid="main-sponsor-card">
            <div className="bg-gradient-to-r from-[#1877F2] to-[#0a1628] p-4 text-white">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div>
                  <h3 className="text-base font-bold mb-1">{sponsors.main.name}</h3>
                  <p className="text-white/80 text-xs">{sponsors.main.description}</p>
                </div>
                <a href={sponsors.main.website} target="_blank" rel="noopener noreferrer" data-testid="main-sponsor-link">
                  <Button className="bg-white text-[#1877F2] px-4 py-2 rounded-full font-bold text-sm">Visit <ExternalLink className="ml-1 h-3 w-3" /></Button>
                </a>
              </div>
            </div>
            <CardContent className="p-3">
              <div className="grid grid-cols-3 gap-3 text-center">
                {[{ icon: Globe, title: 'Global Reach', id: 'global' }, { icon: Award, title: 'Quality Focused', id: 'quality' }, { icon: Heart, title: 'Patient Care', id: 'care' }].map((item) => (
                  <div key={item.id} className="p-2">
                    <item.icon className="w-5 h-5 text-[#1877F2] mx-auto mb-1" />
                    <h4 className="font-bold text-gray-900 text-xs">{item.title}</h4>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Affiliated Sponsors - Compact */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-lg font-bold text-center text-gray-900 mb-4">Affiliated <span className="text-[#1877F2]">Sponsors</span></h2>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {sponsors.affiliates.map((s) => (
              <Card key={s.name} className="border-0 shadow-sm rounded-lg bg-white p-2 flex items-center justify-center h-16 group hover:shadow-md transition-all" data-testid={`affiliate-sponsor-${s.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <img src={s.logo} alt={s.name} className="max-h-8 w-auto object-contain filter grayscale group-hover:grayscale-0 transition-all" />
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Therapeutic Areas - Compact */}
      <section className="py-8 bg-[#F8FBFF]">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-lg font-bold text-center text-gray-900 mb-4">Therapeutic <span className="text-[#1877F2]">Areas</span></h2>
          <div className="flex flex-wrap justify-center gap-2">
            {sponsors.therapeuticAreas.map((area) => (
              <span key={area} className="bg-white border border-[#E6F4FA] rounded-full px-3 py-1.5 text-gray-700 text-xs hover:bg-[#1877F2] hover:text-white transition-all cursor-default">
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - Compact */}
      <section className="py-8 bg-gradient-to-r from-[#1877F2] to-[#0a1628]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-xl font-bold text-white mb-2">Partner With Us</h2>
          <p className="text-white/80 mb-4 text-sm">High recruitment and retention with quality data.</p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Link to="/contact" data-testid="sponsors-contact-btn">
              <Button className="bg-white text-[#1877F2] px-6 py-3 rounded-full font-bold text-sm">Contact Us</Button>
            </Link>
            <a href={`tel:${companyInfo.contact.phone}`} data-testid="sponsors-call-btn">
              <Button variant="outline" className="border-white text-white px-6 py-3 rounded-full font-bold text-sm">
                <Phone className="mr-2 h-4 w-4" />
                {companyInfo.contact.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OurSponsorsPage;
